import type {
	OfferingDataPageSpecification,
	ProductSpecificationBackgroundType,
} from '@sosocio/creator-types/api';
import type {
	OfferingModel,
	ThemeModel,
} from '@sosocio/creator-types/database';
import type { PageModel } from '@sosocio/creator-types/project';
import { OfferingGroups } from 'settings/offerings';

export interface PageBackgroundEditableContext {
	/** Background type declared by the offering's page specification, when it has one. */
	backgroundType?: ProductSpecificationBackgroundType;
	offeringModel?: Pick<OfferingModel, 'groupid'> | null;
	pageModel?: Pick<PageModel, 'bgcolor' | 'bgimage'> | null;
	themeModel?: Pick<ThemeModel, 'color'> | null;
}

/**
 * Resolves the offering whose page specifications describe a page. A virtual offering has no
 * pages of its own, so each page reads the child offering it carries.
 *
 * @param offeringModel Project offering, which may be virtual.
 * @param pageModel Page whose specification is looked up.
 * @returns Offering id for the page specification lookup, or undefined when none is known.
 */
export function getPageSpecificationOfferingId(
	offeringModel?: Pick<OfferingModel, 'id' | 'virtual'> | null,
	pageModel?: Pick<PageModel, 'offeringId'> | null,
): OfferingModel['id'] | undefined {
	if (!offeringModel) {
		return undefined;
	}

	return offeringModel.virtual === 1 ? pageModel?.offeringId : offeringModel.id;
}

/**
 * Reads the background type a page specification declares; only some product kinds define one.
 *
 * @param pageSpecification Offering page specification for the page, if any.
 * @returns The declared background type, or undefined when the specification has none.
 */
export function getPageSpecificationBackgroundType(
	pageSpecification?: OfferingDataPageSpecification,
): ProductSpecificationBackgroundType | undefined {
	if (
		!pageSpecification
		|| !('backgroundType' in pageSpecification)
	) {
		return undefined;
	}

	return pageSpecification.backgroundType;
}

/**
 * Decides whether the user can change a page's background color, which is when the workspace
 * toolbar offers the background tool. A page specification background type wins; otherwise
 * the theme must allow colors on a non-postcard page without a background image.
 *
 * @param context Page, offering, theme and page-specification background type to evaluate.
 * @returns Whether the page background color is user-editable.
 */
export function isPageBackgroundEditable({
	backgroundType,
	offeringModel,
	pageModel,
	themeModel,
}: PageBackgroundEditableContext): boolean {
	if (
		!pageModel
		|| !offeringModel
	) {
		return false;
	}

	if (backgroundType) {
		return backgroundType !== 'transparent';
	}

	return !!(
		!OfferingGroups(
			offeringModel.groupid,
			'PostCards',
		)
		&& pageModel.bgcolor !== 'transparent'
		&& !pageModel.bgimage?.length
		&& themeModel?.color
	);
}
